/**
 * The **Cinema Day** — a cinema's business day, running 04:00 until 03:59:59.999 the next morning,
 * so a late screening belongs to the day it was programmed under rather than the calendar day it
 * ends in. See CONTEXT.md → "Programme & scheduling".
 *
 * This is the only definition of that boundary. It used to exist six times: once here (correct) and
 * once in each of admin, distributor, cinema, cinema-website and b2b-marketing, all five of which
 * ended the day at 03:59:00.000 and so dropped the last 59.999 seconds — a nightly window in which a
 * showing belonged to no Cinema Day at all (CIN-14663).
 */

/** The hour a Cinema Day begins. Everything below derives its boundary from this. */
export const CINEMA_DAY_START_HOUR = 4;

/** 04:00 on the calendar day of `date`. */
export function cinemaDayStart(date: Date | string): Date {
    const start = new Date(date);
    start.setHours(CINEMA_DAY_START_HOUR, 0, 0, 0);
    return start;
}

/** The last instant of the Cinema Day that began on `date` — 03:59:59.999 the following morning. */
export function cinemaDayEnd(date: Date | string): Date {
    const end = new Date(date);
    end.setHours(CINEMA_DAY_START_HOUR - 1, 59, 59, 999);
    end.setDate(end.getDate() + 1);
    return end;
}

/**
 * The Cinema Day `instant` actually falls inside, which before 04:00 is still the previous calendar
 * day's.
 *
 * Anchoring on a raw `new Date()` at 02:00 would produce a window running 04:00 today to 03:59
 * tomorrow — entirely in the future, so anything counting over it reports zero for the four hours
 * after midnight.
 */
export function cinemaDayOf(instant: Date | string): Date {
    const anchor = new Date(instant);
    if (anchor.getHours() < CINEMA_DAY_START_HOUR) {
        anchor.setDate(anchor.getDate() - 1);
    }
    anchor.setHours(CINEMA_DAY_START_HOUR, 0, 0, 0);
    return anchor;
}
